import { useEffect, useRef, useCallback } from 'react';

/**
 * useOnlineInterval — like setInterval, but pauses while the browser is offline.
 *
 * When `offlineFirstEnabled` is true the callback is skipped while
 * navigator.onLine === false and resumes automatically when back online.
 * When `offlineFirstEnabled` is false it behaves as a normal setInterval.
 *
 * @param {Function}  callback              - fn to invoke each tick
 * @param {number}    delayMs               - interval in milliseconds
 * @param {boolean}   offlineFirstEnabled   - feature-gate flag
 * @param {boolean}   [active=true]         - pause/resume externally
 */
export function useOnlineInterval(callback, delayMs, offlineFirstEnabled, active = true) {
  const savedCallback = useRef(callback);
  const intervalRef = useRef(null);

  /* Always hold latest callback without restarting the interval */
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  const start = useCallback(() => {
    if (intervalRef.current) return;           // already running
    intervalRef.current = setInterval(() => {
      savedCallback.current();
    }, delayMs);
  }, [delayMs]);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!active || !delayMs) return;

    /* Determine whether we should be ticking right now */
    const shouldRun = () => {
      if (!offlineFirstEnabled) return true;   // feature off → always run
      return navigator.onLine;                  // feature on  → only online
    };

    if (shouldRun()) start();

    if (offlineFirstEnabled) {
      const handleOnline  = () => { if (active) start(); };
      const handleOffline = () => stop();
      window.addEventListener('online',  handleOnline);
      window.addEventListener('offline', handleOffline);
      return () => {
        stop();
        window.removeEventListener('online',  handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    }

    return stop;
  }, [delayMs, offlineFirstEnabled, active, start, stop]);
}
